import React from 'react';
import { H1, PSmall } from '../../components/elements/Text';
import Navbar from '../../components/Navbar';
import PageWithAsyncData from '../../components/PageWithAsyncData';
import styled, { css } from 'styled-components';
import {
  useGetAssessmentTests,
  useGetCompany,
} from '../../hooks/use-hydra/admin';

import { useParams } from 'react-router-dom';
import { COMPANY_PAGES } from '../../components/Admin/CompanySideNavigation';
import CompanyPageWrapper from '../../components/Admin/CompanyPageWrapper';
import InviteUsersToAssessmentTestModal from '../../components/Admin/Modals/InviteUsersToAssessmentTestModal';
import Table from '../../components/Table/CompactTable';
import { createColumnHelper } from '@tanstack/react-table';
import { SmallPortalTooltip } from '../../components/Tooltip';

import CollapsibleSection, {
  CollapsibleSectionFooter,
  CollapsibleSectionFooterButton,
  CollapsibleSectionHeader,
  CollapsibleSectionLabel,
} from '../../components/Admin/CollapsibleSection';
import { TestAssignment } from '../../api/admin';
import Button from '../../components/elements/Button';
import CreateAssessmentTestCohortModal from '../../components/Admin/Modals/CreateAssessmentTestCohortModal';
import { ROUTES } from '../../routing';
import StatusCell from '../../components/Admin/AssessmenTest/TestStatusCell';
import DateCellContent from '../../components/Admin/AssessmenTest/DateCellContent';
import CopyLink from '../../components/CopyLink';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

const Row = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
`;

const TableWrapper = styled.div<{ enableVirtualization?: boolean }>`
  .table-scroll-wrapper {
    max-height: 50vh;
    ${({ enableVirtualization }) =>
      enableVirtualization &&
      css`
        height: 50vh;
      `}
  }
  .assessment-tests-table {
    th,
    td {
      &:last-child {
        border-right: none !important;
      }
      &:first-child {
        border-left: none !important;
      }
    }

    tbody > tr:last-child > td {
      border-bottom: none;
    }

    th {
      border-radius: 0 !important;
    }
  }
`;

const NavbarWithBreadcrumbs = ({
  name,
  id,
}: {
  name?: string;
  id?: string;
}) => (
  <Navbar
    breadCrumbs={[
      {
        label: 'Home',
        url: ROUTES.HOME.getPath(),
      },
      {
        label: 'Teams',
        url: ROUTES.ADMIN.COMPANIES.getPath(),
      },
      ...(name && id
        ? [
            {
              label: name,
              url: ROUTES.ADMIN.COMPANY.getPath({ companyId: id }),
            },
          ]
        : []),
      { label: 'Assessment Tests', url: null },
    ]}
  />
);

const Content = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 32px;
  margin-bottom: 72px;
  gap: 32px;
`;

const TopBarWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
`;

const EmptyStateTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

export default function AssessmentTests() {
  const { id } = useParams<{ id: string }>();
  const {
    data: companyData,
    isLoading: isLoadingCompany,
    error: errorCompany,
    refetch: refetchCompany,
  } = useGetCompany(parseInt(id));
  const [isAddTestersModalOpen, setIsAddTestersModalOpen] =
    React.useState(false);
  const [isCreateCohortModalOpen, setIsCreateCohortModalOpen] =
    React.useState(false);
  const [cohortId, setCohortId] = React.useState<number | null>(null);
  const {
    data: assessmentTests,
    isLoading: isLoadingAssessmentTests,
    error: errorAssessmentTests,
    refetch: refetchAssessmentTests,
  } = useGetAssessmentTests(parseInt(id));

  const byCohortGroups: Record<number, TestAssignment[]> | undefined =
    React.useMemo(() => {
      if (!assessmentTests) return undefined;
      return assessmentTests.cohorts.reduce((acc, cohort) => {
        const cohortId = cohort.id;
        const assignments = assessmentTests.testAssignments.filter(
          (test) => test.cohort.id === cohortId
        );
        if (!acc[cohortId]) {
          acc[cohortId] = [];
        }
        acc[cohortId].push(...assignments);
        return acc;
      }, {} as Record<number, TestAssignment[]>);
    }, [assessmentTests]);

  const hasCohorts = (assessmentTests?.cohorts?.length ?? 0) > 0;
  const hasTests = (assessmentTests?.tests?.length ?? 0) > 0;

  const sortedCohortGroups = React.useMemo(() => {
    if (!byCohortGroups || !assessmentTests) return [];
    const { cohorts } = assessmentTests;
    return Object.entries(byCohortGroups).sort(([id1], [id2]) => {
      const aCohortStartedAt = cohorts.find(
        (cohort) => cohort.id === parseInt(id1)
      )?.createdAt;
      const bCohortStartedAt = cohorts.find(
        (cohort) => cohort.id === parseInt(id2)
      )?.createdAt;
      return dayjs.utc(aCohortStartedAt).isBefore(bCohortStartedAt) ? 1 : -1;
    });
  }, [byCohortGroups, assessmentTests]);

  return (
    <PageWithAsyncData
      isLoading={isLoadingCompany || isLoadingAssessmentTests}
      error={errorCompany || errorAssessmentTests}
      retry={refetchCompany || refetchAssessmentTests}
      noScroll
    >
      <NavbarWithBreadcrumbs name={companyData?.name} id={id} />
      <CompanyPageWrapper
        currentPage={COMPANY_PAGES.ASSESSMENT_TESTS}
        shouldAddEmptyDiv={false}
      >
        <H1>Testers Management</H1>
        <Content style={{ marginBottom: 0 }}>
          <TopBarWrapper>
            <Button
              label="Create Cohort"
              type="secondary"
              showIconBeforeLabel
              faIcon="fa-solid fa-plus"
              style={{ marginTop: 0, padding: '8px 16px' }}
              onClick={() => setIsCreateCohortModalOpen(true)}
            />
          </TopBarWrapper>
          {(!hasTests || !hasCohorts) && (
            <div>
              {!hasCohorts && (
                <PSmall style={{ marginTop: 0 }}>
                  There are no cohorts set up for this company. Create a cohort
                  to assign assessment tests to.
                </PSmall>
              )}
              {!hasTests && (
                <PSmall style={{ marginTop: 0 }}>
                  There are no assessment tests set up for this company.
                </PSmall>
              )}
            </div>
          )}

          {byCohortGroups &&
            sortedCohortGroups.map(([cohortId, tests], index) => (
              <CohortSection
                teamSlug={companyData?.slug ?? ''}
                cohortId={parseInt(cohortId)}
                isFirstCohort={index === 0}
                testAssignments={tests}
                cohortName={
                  assessmentTests?.cohorts.find(
                    (cohort) => cohort.id === parseInt(cohortId)
                  )?.name ?? ''
                }
                hasTests={hasTests}
                onInviteTestees={() => {
                  setIsAddTestersModalOpen(true);
                  setCohortId(parseInt(cohortId));
                }}
              />
            ))}
        </Content>
      </CompanyPageWrapper>

      <InviteUsersToAssessmentTestModal
        isOpen={isAddTestersModalOpen}
        onClose={() => setIsAddTestersModalOpen(false)}
        companyId={parseInt(id)}
        cohortId={cohortId ?? 0}
      />

      <CreateAssessmentTestCohortModal
        isOpen={isCreateCohortModalOpen}
        onClose={() => setIsCreateCohortModalOpen(false)}
        companyId={parseInt(id)}
      />
    </PageWithAsyncData>
  );
}

const columnHelper = createColumnHelper<TestAssignment>();

const createColumns = ({ teamSlug }: { teamSlug: string }) => [
  columnHelper.display({
    id: 'learner',
    header: 'Name',
    cell: ({ row }) => {
      return (
        <>
          <div
            className={`learner-${row.original?.assessmentTestSessionId}`}
            style={{
              width: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {row.original?.user?.name ?? '-'}
          </div>
          <SmallPortalTooltip
            anchorSelect={`.learner-${row.original?.assessmentTestSessionId}`}
          >
            {row.original?.user?.email}
          </SmallPortalTooltip>
        </>
      );
    },
    size: 200,
    meta: {
      styles: {
        minWidth: '200px',
        maxWidth: '200px',
      },
    },
  }),
  columnHelper.display({
    id: 'name',
    header: 'Test',
    cell: ({ row }) => {
      return <div>{row.original?.name ?? '-'}</div>;
    },
    size: 200,
    enableSorting: true,
    meta: {
      styles: {
        minWidth: '250px',
        maxWidth: '250px',
      },
    },
  }),
  columnHelper.display({
    id: 'status',
    header: 'Status',
    cell: StatusCell,
    size: 100,
    meta: {
      styles: {
        minWidth: '120px',
        maxWidth: '120px',
      },
    },
  }),
  columnHelper.display({
    id: 'invite-link',
    header: 'Invite Link',
    cell: () => {
      return (
        <CopyLink
          link={ROUTES.TEAMS.TEST_INVITE.getURL({
            teamSlug: teamSlug,
          })}
        />
      );
    },
    size: 250,
    meta: {
      styles: {
        minWidth: '250px',
        maxWidth: '250px',
      },
    },
  }),
  columnHelper.accessor('invitedAt', {
    header: 'Added At',
    cell: (props) => (
      <DateCellContent date={props.getValue()} label="Invited At" />
    ),
    meta: {
      styles: {
        minWidth: '180px',
      },
    },
  }),
  columnHelper.accessor('startedAt', {
    header: 'Started At',
    cell: (props) => (
      <DateCellContent date={props.getValue()} label="Started At" />
    ),
    meta: {
      styles: {
        minWidth: '180px',
      },
    },
  }),

  columnHelper.accessor('finishedAt', {
    header: 'Finished At',
    cell: (props) => (
      <DateCellContent date={props.getValue()} label="Finished At" />
    ),
    meta: {
      styles: {
        minWidth: '180px',
      },
    },
  }),
  columnHelper.accessor('reviewedAt', {
    header: 'Reviewed At',
    cell: (props) => (
      <DateCellContent date={props.getValue()} label="Reviewed At" />
    ),
    meta: {
      styles: {
        minWidth: '180px',
      },
    },
  }),
];

const CohortSection = ({
  cohortId,
  teamSlug,
  testAssignments,
  isFirstCohort,
  cohortName,
  hasTests,
  onInviteTestees,
}: {
  cohortId: number;
  teamSlug: string;
  testAssignments: TestAssignment[];
  isFirstCohort: boolean;
  cohortName: string;
  hasTests: boolean;
  onInviteTestees: () => void;
}) => {
  const [isExpanded, setIsExpanded] = React.useState(isFirstCohort);
  const shouldVirtualize = testAssignments.length > 20;

  return (
    <CollapsibleSection
      headerLeft={
        <CollapsibleSectionHeader>{cohortName}</CollapsibleSectionHeader>
      }
      headerRight={null}
      expanded={isExpanded}
      onToggle={() => {
        setIsExpanded(!isExpanded);
      }}
    >
      <TableWrapper enableVirtualization={shouldVirtualize}>
        {testAssignments.length > 0 ? (
          <Table
            data={testAssignments}
            shouldPinHeaderRow
            initialPinning={{
              left: ['learner'],
            }}
            getRowId={(row) => row.assessmentTestSessionId.toString()}
            columns={createColumns({ teamSlug })}
            className="assessment-tests-table"
            enableVirtualization={shouldVirtualize}
            virtualizationRowHeight={50}
            footer={
              <TableFooter
                testAssignments={testAssignments}
                cohortId={cohortId}
                hasTests={hasTests}
                onInviteTestees={onInviteTestees}
              />
            }
          />
        ) : (
          <>
            <div style={{ margin: '16px' }}>
              <EmptyStateTitle>No testers invited yet</EmptyStateTitle>
              <PSmall style={{ marginTop: 0, paddingBottom: 0 }}>
                Invite people to this cohort to get started.
              </PSmall>
            </div>
            <TableFooter
              testAssignments={testAssignments}
              cohortId={cohortId}
              hasTests={hasTests}
              onInviteTestees={onInviteTestees}
            />
          </>
        )}
      </TableWrapper>
    </CollapsibleSection>
  );
};

const TableFooter = ({
  testAssignments,
  cohortId,
  hasTests,
  onInviteTestees,
}: {
  testAssignments: TestAssignment[];
  cohortId: number | null;
  hasTests: boolean;
  onInviteTestees: () => void;
}) => {
  return (
    <CollapsibleSectionFooter>
      <CollapsibleSectionLabel>
        {testAssignments.length}{' '}
        {testAssignments.length === 1 ? 'Test' : 'Tests'}
      </CollapsibleSectionLabel>
      <Row>
        <CollapsibleSectionFooterButton
          label="Invite Testees"
          faIcon="fa-solid fa-plus"
          showIconBeforeLabel
          isDisabled={!cohortId || !hasTests}
          tooltip={
            !hasTests
              ? 'Please create an assessment test first.'
              : 'Upload a CSV or add new testees manually.'
          }
          onClick={() => {
            onInviteTestees();
          }}
        />
      </Row>
    </CollapsibleSectionFooter>
  );
};
